import { useEffect, useState } from 'react';

export interface LoadedImage {
  /** Decoded element, ready for `drawImage`. */
  readonly element: HTMLImageElement;
  /** Data URL of the same bytes, used by the SVG exporter. */
  readonly dataUrl: string;
}

export interface ImageFileState {
  readonly image: LoadedImage | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

const IDLE: ImageFileState = { image: null, isLoading: false, error: null };

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      const { result } = reader;
      if (typeof result === 'string') resolve(result);
      else reject(new Error('Die Datei konnte nicht gelesen werden.'));
    });
    reader.addEventListener('error', () => {
      reject(new Error('Die Datei konnte nicht gelesen werden.'));
    });
    reader.readAsDataURL(file);
  });
}

function decode(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const element = new Image();
    element.addEventListener('load', () => {
      resolve(element);
    });
    element.addEventListener('error', () => {
      reject(new Error('Das Bild konnte nicht geladen werden.'));
    });
    element.src = dataUrl;
  });
}

/**
 * Decodes an uploaded image, handling SVG and raster formats identically.
 *
 * Reading to a data URL rather than an object URL means there is nothing to
 * revoke — the previous version leaked one object URL per upload for the
 * lifetime of the document — and it gives the SVG exporter an embeddable
 * `href` for free. Every effect run is guarded by a cancellation flag, so
 * swapping files quickly can no longer land a stale image last.
 */
export function useImageFile(file: File | null): ImageFileState {
  const [state, setState] = useState<ImageFileState>(IDLE);

  useEffect(() => {
    if (file === null) {
      setState(IDLE);
      return;
    }

    // An AbortController rather than a boolean flag: the signal is opaque to
    // control-flow analysis, so the post-await checks below stay meaningful.
    const controller = new AbortController();
    setState({ image: null, isLoading: true, error: null });

    void (async () => {
      try {
        const dataUrl = await readAsDataUrl(file);
        const element = await decode(dataUrl);
        if (controller.signal.aborted) return;
        setState({ image: { element, dataUrl }, isLoading: false, error: null });
      } catch (cause) {
        if (controller.signal.aborted) return;
        const message =
          cause instanceof Error ? cause.message : 'Das Bild konnte nicht geladen werden.';
        setState({ image: null, isLoading: false, error: message });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [file]);

  return state;
}
